import React from 'react';
import { motion } from 'framer-motion';
import { BsCheckCircle, BsFileEarmarkText, BsCurrencyDollar } from 'react-icons/bs';

interface ActivityFeedTabProps {
    activities?: any[];
}

const ActivityFeedTab: React.FC<ActivityFeedTabProps> = ({ activities = [] }) => {
    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-8">
            <div className="flex justify-between items-center mb-8 pb-6 border-b border-white/10">
                <div>
                    <h3 className="text-xl font-bold text-white mb-1">Activity Feed</h3>
                    <p className="text-sm text-white/50">Unified timeline of all project interactions and alerts.</p>
                </div>
                <div className="flex gap-2">
                    <button className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-black bg-premium-gold rounded hover:bg-white transition-colors">
                        Require Action (2)
                    </button>
                    <button className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white border border-white/20 rounded hover:bg-white/10 transition-colors">
                        All Activity
                    </button>
                </div>
            </div>

            <div className="relative border-l-2 border-white/10 ml-4 space-y-10 pb-6">
                {activities.length === 0 ? (
                    <div className="text-center text-white/50 italic py-8 border border-white/5 bg-black/20 rounded-lg mr-4">
                        No recent activity found. Publish projects and collaborate to see updates here.
                    </div>
                ) : (
                    activities.map((activity, index) => (
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            key={activity.id}
                            className="relative pl-8 group"
                        >
                            {/* Timeline Dot */}
                            <div className="absolute -left-[1.1rem] top-1 bg-premium-wood-dark border-4 border-black w-8 h-8 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(0,0,0,0.5)] group-hover:scale-110 group-hover:border-white/20 transition-all">
                                {activity.type === 'milestone' && <BsCurrencyDollar className="text-premium-gold" />}
                                {activity.type === 'application' && <BsFileEarmarkText className="text-blue-400" />}
                                {(!['milestone', 'application'].includes(activity.type)) && <BsCheckCircle className="text-premium-gold" />}
                            </div>

                            <div className={`bg-black/40 border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors ${activity.actionReq ? 'border-premium-gold/30 shadow-[0_0_15px_rgba(212,175,55,0.05)]' : ''}`}>
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-3">
                                        <h4 className="font-bold text-white text-base">{activity.title}</h4>
                                        {activity.actionReq && (
                                            <span className="text-[10px] font-bold uppercase bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/20 animate-pulse">
                                                Action Required
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-xs text-white/40 whitespace-nowrap">{activity.time}</span>
                                </div>

                                <div className="flex items-center gap-2 text-xs text-premium-gold mb-4">
                                    <span>{activity.project}</span>
                                    {activity.talent && (
                                        <>
                                            <span>•</span>
                                            <span className="text-white/60">From: {activity.talent}</span>
                                        </>
                                    )}
                                </div>

                                <p className="text-sm text-white/70 leading-relaxed mb-4">
                                    {activity.description || (
                                        activity.type === 'milestone'
                                            ? `A new update occurred on ${activity.project} reporting a status of ${activity.actionReq ? 'completed. Please approve the release of funds.' : 'funded and active.'}`
                                            : activity.type === 'application'
                                                ? `A new candidate applied for the role in ${activity.project}. Review their profile to proceed.`
                                                : "Activity recorded."
                                    )}
                                </p>

                                {activity.actionReq && (
                                    <div className="flex gap-3 pt-4 border-t border-white/5">
                                        <button className="px-4 py-2 bg-premium-gold text-black text-xs font-bold rounded hover:bg-white transition-colors">
                                            {activity.type === 'milestone' ? 'Review Milestone' : 'Review Candidate'}
                                        </button>
                                        <button className="px-4 py-2 border border-white/20 text-white text-xs font-bold rounded hover:bg-white/10 transition-colors">
                                            Dismiss
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))
                )}
            </div>

            <div className="text-center mt-6 pt-6 border-t border-white/10">
                <button className="text-sm text-premium-gold hover:underline">Load Older Activity</button>
            </div>
        </div>
    );
};

export default ActivityFeedTab;
